import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { NearestLocationFinder } from '../components/NearestLocationFinder';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, PrinterIcon, ChevronDownIcon } from 'lucide-react';
const BOOKING_URL = 'https://appointments.4patientcare.app/?CoverKey=5118&Source=website%20WebSchedV4&ReferredBy=Unknon&Gcount=0';
const locations = [{
  name: 'Mitchellville',
  subtitle: 'Main Office',
  phone: '301-324-9500',
  fax: '301-324-9502',
  email: 'mitchellville@visualeyesgroup.com',
  address: '10240 Lake Arbor Way',
  cityStateZip: 'Mitchellville, MD 20721',
  hours: [{
    days: 'Monday - Friday',
    time: '9:30 AM - 7:00 PM'
  }, {
    days: 'Saturday',
    time: '9:00 AM - 3:30 PM'
  }]
}, {
  name: 'Clinton',
  phone: '301-877-1770',
  fax: '301-877-3207',
  email: 'clinton@visualeyesgroup.com',
  address: '8931 Woodyard Rd',
  cityStateZip: 'Clinton, MD 20735',
  hours: [{
    days: 'Monday - Friday',
    time: '9:30 AM - 7:00 PM'
  }, {
    days: 'Saturday',
    time: '9:00 AM - 3:30 PM'
  }]
}, {
  name: 'Greenbelt',
  phone: '301-614-3937',
  fax: '301-614-2909',
  email: 'greenbelt@visualeyesgroup.com',
  address: '6088 Greenbelt Road Unit 44',
  cityStateZip: 'Greenbelt, MD 20770',
  hours: [{
    days: 'Monday - Friday',
    time: '10:00 AM - 7:00 PM'
  }, {
    days: 'Saturday',
    time: '10:00 AM - 4:00 PM'
  }]
}, {
  name: 'Hyattsville',
  phone: '301-434-3937',
  fax: '301-434-3997',
  email: 'hyattsville@visualeyesgroup.com',
  address: '1835 University Blvd. Ste 228',
  cityStateZip: 'Hyattsville, MD 20783',
  hours: [{
    days: 'Monday - Friday',
    time: '9:30 AM - 6:30 PM'
  }, {
    days: 'Saturday',
    time: '9:00 AM - 4:00 PM'
  }]
}, {
  name: 'Silver Spring',
  phone: '301-681-9797',
  fax: '301-681-6855',
  email: 'silverspring@visualeyesgroup.com',
  address: '8121 Georgia Ave, Ste 800',
  cityStateZip: 'Silver Spring, MD 20910',
  hours: [{
    days: 'Monday - Friday',
    time: '9:30 AM - 6:30 PM'
  }, {
    days: 'Saturday',
    time: '9:00 AM - 4:00 PM'
  }]
}, {
  name: 'Frederick',
  phone: '301-694-7557',
  fax: '301-694-7525',
  email: 'frederick@visualeyesgroup.com',
  address: '61 Thomas Johnson Dr, Ste 201',
  cityStateZip: 'Frederick, MD 21702',
  hours: [{
    days: 'Monday - Friday',
    time: '9:30 AM - 6:30 PM'
  }, {
    days: 'Saturday',
    time: '9:00 AM - 3:30 PM'
  }]
}];
export function LocationsPage() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const toggleAccordion = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };
  return <>
      <PageHeader title="Our Locations" backgroundImage="https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=1920&q=80" data-id="element-980" />
      <NearestLocationFinder data-id="element-981" />

      <section className="py-20 md:py-28 bg-surface-muted" data-id="element-982">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-983">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-984">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-section-mobile md:text-section text-brand-dark mb-4" data-id="element-985">
              6 Locations Across Maryland, Serving Your Eyecare Needs
            </motion.h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4" data-id="element-986">
            {locations.map((location, index) => {
            const isExpanded = expandedIndex === index;
            return <motion.div key={location.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-white rounded-card shadow-card border border-gray-100 overflow-hidden" data-id="element-987">
                  {/* Accordion Header */}
                  <button onClick={() => toggleAccordion(index)} className="w-full text-left p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-gray-50 transition-colors focus-ring" aria-expanded={isExpanded} data-id="element-988">
                    <div data-id="element-989">
                      <div className="flex items-center gap-3 mb-1" data-id="element-990">
                        <h3 className="text-xl font-heading font-bold text-brand-dark" data-id="element-991">
                          {location.name}
                        </h3>
                        {location.subtitle && <span className="bg-brand-gold/10 text-brand-gold text-[10px] uppercase font-bold px-2 py-1 rounded-sm tracking-wider" data-id="element-992">
                            {location.subtitle}
                          </span>}
                      </div>
                      <p className="text-sm text-neutral-text/60" data-id="element-993">
                        {location.address}, {location.cityStateZip}
                      </p>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-6 w-full sm:w-auto" data-id="element-994">
                      <span className="text-brand-dark font-medium" data-id="element-995">
                        {location.phone}
                      </span>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-brand-gold/10 text-brand-gold transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-996">
                        <ChevronDownIcon className="w-5 h-5" data-id="element-997" />
                      </div>
                    </div>
                  </button>

                  {/* Accordion Body */}
                  <AnimatePresence initial={false} data-id="element-998">
                    {isExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} className="overflow-hidden" data-id="element-999">
                        <div className="p-6 pt-0 border-t border-gray-100 mt-2" data-id="element-1000">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6" data-id="element-1001">
                            {/* Left Column: Contact & Address */}
                            <div className="space-y-6" data-id="element-1002">
                              <div className="space-y-4" data-id="element-1003">
                                <div className="flex items-start gap-3" data-id="element-1004">
                                  <PhoneIcon className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-1005" />
                                  <div data-id="element-1006">
                                    <span className="text-sm text-neutral-text/60 block mb-0.5" data-id="element-1007">
                                      Phone
                                    </span>
                                    <a href={`tel:${location.phone.replace(/-/g, '')}`} className="text-brand-dark font-medium hover:text-brand-gold transition-colors" data-id="element-1008">
                                      {location.phone}
                                    </a>
                                  </div>
                                </div>

                                <div className="flex items-start gap-3" data-id="element-1009">
                                  <PrinterIcon className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-1010" />
                                  <div data-id="element-1011">
                                    <span className="text-sm text-neutral-text/60 block mb-0.5" data-id="element-1012">
                                      Fax
                                    </span>
                                    <span className="text-brand-dark font-medium" data-id="element-1013">
                                      {location.fax}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-start gap-3" data-id="element-1014">
                                  <MailIcon className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-1015" />
                                  <div data-id="element-1016">
                                    <span className="text-sm text-neutral-text/60 block mb-0.5" data-id="element-1017">
                                      Email
                                    </span>
                                    <a href={`mailto:${location.email}`} className="text-brand-dark font-medium hover:text-brand-gold transition-colors break-all" data-id="element-1018">
                                      {location.email}
                                    </a>
                                  </div>
                                </div>
                              </div>

                              <hr className="border-gray-100" data-id="element-1019" />

                              <div className="flex items-start gap-3" data-id="element-1020">
                                <MapPinIcon className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-1021" />
                                <div data-id="element-1022">
                                  <span className="text-sm text-neutral-text/60 block mb-0.5" data-id="element-1023">
                                    Address
                                  </span>
                                  <a href={`https://maps.google.com/?q=${encodeURIComponent(`${location.address}, ${location.cityStateZip}`)}`} target="_blank" rel="noreferrer" className="text-brand-dark font-medium hover:text-brand-gold transition-colors" data-id="element-1024">
                                    {location.address}
                                    <br data-id="element-1025" />
                                    {location.cityStateZip}
                                  </a>
                                </div>
                              </div>
                            </div>

                            {/* Right Column: Hours & CTA */}
                            <div className="flex flex-col h-full" data-id="element-1026">
                              <div className="flex items-start gap-3 mb-8" data-id="element-1027">
                                <ClockIcon className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-1028" />
                                <div className="w-full" data-id="element-1029">
                                  <span className="text-sm text-neutral-text/60 block mb-2" data-id="element-1030">
                                    Office Hours
                                  </span>
                                  <ul className="space-y-2 w-full" data-id="element-1031">
                                    {location.hours.map((hour, i) => <li key={i} className="flex justify-between text-sm text-brand-dark font-medium border-b border-gray-50 pb-2 last:border-0 last:pb-0" data-id="element-1032">
                                        <span data-id="element-1033">{hour.days}</span>
                                        <span data-id="element-1034">{hour.time}</span>
                                      </li>)}
                                  </ul>
                                </div>
                              </div>

                              <div className="mt-auto pt-4" data-id="element-1035">
                                <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="block w-full text-center bg-brand-dark text-white py-3.5 rounded-button font-heading font-semibold hover:bg-brand-gold transition-colors focus-ring shadow-md hover:shadow-lg" data-id="element-1036">
                                  Make an Appointment
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </div>
        </div>
      </section>
    </>;
}